/**
 * JobProgress Component
 * One-line job status indicator (no expand). Used when no custom prompt form is provided.
 * Custom forms (content-prompt) use JobDetailsBar inline instead.
 */

import React, { useMemo } from 'react';
import type { StepStatus, JobStatus } from '@ai-content-hub/shared-api';
import type { JobProgressProps, AIGeneratorColors } from './AIContentGenerator.types';
import { DEFAULT_COLORS } from './AIContentGenerator.types';
import { useAIGeneratorTranslations } from './i18n';

// Status icons as SVG
const StatusIcon: React.FC<{ status: StepStatus | JobStatus; size?: number; colors: AIGeneratorColors }> = ({
  status,
  size = 20,
  colors,
}) => {
  const iconProps = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };

  switch (status) {
    case 'completed':
      return (
        <svg {...iconProps} style={{ color: colors.success }}>
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      );
    case 'running':
      return (
        <svg
          {...iconProps}
          style={{ color: colors.primary, animation: 'job-spin 1s linear infinite' }}
        >
          <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
          <path d="M12 2a10 10 0 0 1 10 10" />
        </svg>
      );
    case 'pending':
      return (
        <svg {...iconProps} style={{ color: colors.textMuted }}>
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      );
    case 'failed':
      return (
        <svg {...iconProps} style={{ color: colors.error }}>
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      );
    case 'skipped':
      return (
        <svg {...iconProps} style={{ color: colors.textMuted }}>
          <circle cx="12" cy="12" r="10" />
          <line x1="8" y1="12" x2="16" y2="12" />
        </svg>
      );
    case 'cancelled':
      return (
        <svg {...iconProps} style={{ color: 'var(--color-warning, #f59e0b)' }}>
          <circle cx="12" cy="12" r="10" />
          <rect x="9" y="9" width="6" height="6" />
        </svg>
      );
    default:
      return (
        <svg {...iconProps} style={{ color: colors.textMuted }}>
          <circle cx="12" cy="12" r="10" />
        </svg>
      );
  }
};

const formatDuration = (seconds: number): string => {
  if (seconds < 1) return '<1s';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
};

export const JobProgress: React.FC<JobProgressProps> = ({
  jobStatus,
  isPolling = false,
  onRetry,
  onDismiss,
  colors: colorsProp,
  className,
}) => {
  const colors: AIGeneratorColors = useMemo(
    () => ({ ...DEFAULT_COLORS, ...colorsProp }),
    [colorsProp]
  );

  const t = useAIGeneratorTranslations();

  const status = jobStatus?.status;
  const statusTitle = useMemo(() => {
    if (!status) return '';
    switch (status) {
      case 'completed': return t.generationComplete;
      case 'failed': return t.generationFailed;
      case 'cancelled': return t.generationCancelled;
      default: return t.generating;
    }
  }, [status, t]);

  if (!jobStatus) {
    return null;
  }

  const { progress, error, duration_seconds } = jobStatus;
  const isTerminal = status === 'completed' || status === 'failed' || status === 'cancelled';

  return (
    <div
      className={`job-progress ${className || ''}`}
      style={{
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        background: colors.surface,
        overflow: 'hidden',
      }}
    >
      {/* One-line status (no expand) */}
      <div
        className="job-progress-header"
        style={{
          width: '100%',
          padding: '10px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
          <StatusIcon status={status} size={18} colors={colors} />
          <span
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: colors.text,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {statusTitle}
          </span>
          {error && status === 'failed' && (
            <span
              style={{
                fontSize: 12,
                color: colors.error,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 180,
              }}
              title={error}
            >
              · {error.length > 40 ? `${error.slice(0, 37)}…` : error}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {progress && !isTerminal && (
            <span style={{ fontSize: 12, color: colors.textMuted, fontVariantNumeric: 'tabular-nums' }}>
              {/* EAA-2031: steps are pipeline stages, not questions — show the percent only. */}
              {Math.round(progress.percentage)}%
            </span>
          )}
          {isTerminal && duration_seconds > 0 && (
            <span style={{ fontSize: 12, color: colors.textMuted }}>{formatDuration(duration_seconds)}</span>
          )}
          {isTerminal && (onRetry || onDismiss) && (
            <div
              style={{ display: 'flex', gap: 4 }}
              onClick={(e) => e.stopPropagation()}
            >
              {status === 'failed' && onRetry && (
                <button
                  type="button"
                  className="job-progress-retry-button"
                  onClick={onRetry}
                  style={{
                    padding: '4px 8px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: 4,
                    background: colors.background,
                    color: colors.text,
                    fontSize: 11,
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  {t.retryButton}
                </button>
              )}
              {onDismiss && (
                <button
                  type="button"
                  className="job-progress-dismiss-button"
                  onClick={onDismiss}
                  style={{
                    padding: '4px 8px',
                    border: 'none',
                    borderRadius: 4,
                    background: status === 'completed' ? colors.success : colors.textMuted,
                    color: 'var(--color-text-on-cta, #ffffff)',
                    fontSize: 11,
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  {status === 'completed' ? t.continueButton : t.dismissButton}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Polling indicator */}
      {isPolling && !isTerminal && (
        <div
          className="job-progress-polling"
          style={{
            padding: '6px 12px',
            borderTop: `1px solid ${colors.border}`,
            background: `${colors.info}08`,
            fontSize: 11,
            color: colors.info,
            textAlign: 'center',
          }}
        >
          {t.checkingStatus}
        </div>
      )}

      {/* CSS for spinner animation */}
      <style>{`
        @keyframes job-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default JobProgress;
